import { useEffect, useMemo, useRef, useState } from 'react';
import { StanButton } from '@/components';
import IcPlus from '@/assets/icons/ic-plus.svg';
import { DRAFT_FUNNEL_PAGE_ID } from '../../../constants/funnels.constants';
import {
    buildFunnelPageTree,
    buildFunnelPageTreePositions,
    type FunnelPageSlot,
    getFunnelPageRelationships,
} from '../../../logic';
import { useFunnelEditorStore } from '../../../stores/funnelEditorStore';
import { ManageFunnelPagesModal } from '../modals/ManageFunnelPagesModal';
import { getSuggestedPageAt } from '../../list/expanded/FunnelPagesExpandedLayout';
import type { FunnelPageItem, FunnelPageProductInfo } from '../../../types';
import { FunnelPageNavGroup } from './FunnelPageNavGroup';
import { SuggestedFunnelPageNavRow } from './SuggestedFunnelPageNavRow';

interface FunnelPagesNavigationProps {
    pages: FunnelPageItem[];
    selectedPageId: number | null;
    selectedSuggestedIndex: number | null;
    onSelectPage: (pageId: number) => void;
    onSelectSuggested: (index: number) => void;
    onAddPage: () => void;
    onReorderPages: (orderedSavedPageIds: number[]) => void;
    onHidePage: (pageId: number) => void;
    onDuplicatePage: (pageId: number) => void;
    onDeletePage: (pageId: number) => void;
    onPreview: () => void;
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    /** Minimum visible row count. Driven by the template the funnel was created with. */
    minRows: number;
    isAddDisabled?: boolean;
    addDisabledReason?: string;
    isDeleting?: boolean;
}

type NavSlot =
    | (Extract<FunnelPageSlot, { kind: 'page' }> & { navStep: number })
    | { kind: 'draft'; stepNumber: number; page: FunnelPageItem }
    | { kind: 'suggested'; stepNumber: number; isLast: boolean };

export const FunnelPagesNavigation = ({
    pages,
    selectedPageId,
    selectedSuggestedIndex,
    onSelectPage,
    onSelectSuggested,
    onAddPage,
    onReorderPages,
    onHidePage,
    onDuplicatePage,
    onDeletePage,
    onPreview,
    productInfoByPageId,
    minRows,
    isAddDisabled = false,
    addDisabledReason,
    isDeleting = false,
}: FunnelPagesNavigationProps) => {
    const [showManageModal, setShowManageModal] = useState(false);
    const [newlyAddedPageId, setNewlyAddedPageId] = useState<number | null>(null);

    const dismissedSuggestedCount = useFunnelEditorStore(s => s.dismissedSuggestedCount);
    const dismissSuggested = useFunnelEditorStore(s => s.dismissSuggested);

    const savedPages = pages.filter(p => p.page_id !== DRAFT_FUNNEL_PAGE_ID);

    // Briefly highlight a newly-appearing saved page (duplicate, publish) with
    // a bounce-in animation so it's obvious where the new row landed.
    const prevSavedIdsRef = useRef<Set<number> | null>(null);
    useEffect(() => {
        const currentIds = new Set(savedPages.map(p => p.page_id));
        const prev = prevSavedIdsRef.current;
        prevSavedIdsRef.current = currentIds;

        if (!prev) return undefined;
        const added = [...currentIds].filter(id => !prev.has(id));
        if (added.length !== 1) return undefined;

        const id = added[0] || 0;
        setNewlyAddedPageId(id);
        const timer = setTimeout(() => {
            setNewlyAddedPageId(current => (current === id ? null : current));
        }, 600);
        return () => clearTimeout(timer);
    }, [savedPages]);

    const draftPage = pages.find(p => p.page_id === DRAFT_FUNNEL_PAGE_ID) ?? null;
    const canManage = savedPages.length > 0;

    const rawSlots = useMemo(() => buildFunnelPageTree(savedPages), [savedPages]);
    const relationshipByPageId = useMemo(() => getFunnelPageRelationships(savedPages), [savedPages]);
    const positionByPageId = useMemo(() => buildFunnelPageTreePositions(rawSlots), [rawSlots]);

    const effectiveMinRows = Math.max(1, minRows - dismissedSuggestedCount);

    const mergedSlots = useMemo<NavSlot[]>(() => {
        const realSlots = rawSlots.filter((s): s is Extract<FunnelPageSlot, { kind: 'page' }> => s.kind === 'page');
        const totalSavedPageCount = positionByPageId.size + (draftPage ? 1 : 0);
        const slots: NavSlot[] = realSlots.map(s => ({ ...s, navStep: positionByPageId.get(s.page.page_id) ?? 0 }));

        if (draftPage) slots.push({ kind: 'draft', stepNumber: totalSavedPageCount, page: draftPage });

        for (let step = totalSavedPageCount + 1; step <= effectiveMinRows; step++) {
            slots.push({ kind: 'suggested', stepNumber: step, isLast: step === effectiveMinRows });
        }
        return slots;
    }, [rawSlots, draftPage, positionByPageId, effectiveMinRows]);

    const totalPageCount = Math.max(positionByPageId.size + (draftPage ? 1 : 0), effectiveMinRows);

    const selectedPage = selectedPageId !== null ? pages.find(p => p.page_id === selectedPageId) : null;
    const currentStep = (() => {
        if (selectedSuggestedIndex !== null) return selectedSuggestedIndex + 1;
        if (selectedPage) {
            if (selectedPage.page_id === DRAFT_FUNNEL_PAGE_ID) return positionByPageId.size + 1;
            return positionByPageId.get(selectedPage.page_id) ?? selectedPage.order;
        }
        return totalPageCount;
    })();

    return (
        <div className='w-full md:shrink-0 xl:max-w-md'>
            <div className='bg-canvas shadow-stan flex flex-col gap-4 rounded-2xl p-4 md:gap-6 md:p-7'>
                <div className='flex w-full items-center justify-between gap-3'>
                    <span className='text-heading-h3 text-dark'>
                        {totalPageCount === 0 ? 'No pages yet' : `Page ${currentStep} of ${totalPageCount}`}
                    </span>

                    <div className='flex items-center gap-2'>
                        {canManage && (
                            <button
                                onClick={onPreview}
                                className='border-brand-main text-brand-main hover:bg-brand-subtle focus-visible:ring-brand-subtle cursor-pointer rounded-full border px-2 py-1 text-xs font-medium transition-colors focus-visible:ring-2'
                            >
                                Preview
                            </button>
                        )}
                        {canManage && (
                            <button
                                onClick={() => setShowManageModal(true)}
                                className='border-brand-main text-brand-main hover:bg-brand-subtle focus-visible:ring-brand-subtle cursor-pointer rounded-full border px-2 py-1 text-xs font-medium transition-colors focus-visible:ring-2'
                            >
                                Manage
                            </button>
                        )}
                    </div>
                </div>

                <div className='flex flex-col gap-3'>
                    {mergedSlots.map(slot => {
                        if (slot.kind === 'suggested') {
                            // Step 1 is the entry page — it's mandatory and
                            // should never be dismissable.
                            const canDismiss = slot.stepNumber > 1;
                            return (
                                <SuggestedFunnelPageNavRow
                                    key={`suggested-${slot.stepNumber}`}
                                    stepNumber={slot.stepNumber}
                                    title={getSuggestedPageAt(slot.stepNumber - 1).title}
                                    active={selectedSuggestedIndex === slot.stepNumber - 1}
                                    onSelect={() => onSelectSuggested(slot.stepNumber - 1)}
                                    onDismiss={canDismiss ? dismissSuggested : undefined}
                                />
                            );
                        }

                        if (slot.kind === 'draft') {
                            return (
                                <SuggestedFunnelPageNavRow
                                    key={`draft-${slot.stepNumber}`}
                                    stepNumber={slot.stepNumber}
                                    title={getSuggestedPageAt(slot.stepNumber - 1).title}
                                    active={slot.page.page_id === selectedPageId}
                                    onSelect={() => onSelectPage(slot.page.page_id)}
                                />
                            );
                        }

                        return (
                            <FunnelPageNavGroup
                                key={slot.page.page_id}
                                slot={slot}
                                parentStepNumber={null}
                                selectedPageId={selectedPageId}
                                productInfoByPageId={productInfoByPageId}
                                relationshipByPageId={relationshipByPageId}
                                positionByPageId={positionByPageId}
                                onSelectPage={onSelectPage}
                                newlyAddedPageId={newlyAddedPageId}
                            />
                        );
                    })}

                    <div className='mt-3 flex flex-col gap-2'>
                        <StanButton
                            name='add-funnel-page'
                            variant='outline'
                            size='sm'
                            icon={IcPlus}
                            onClick={onAddPage}
                            disabled={isAddDisabled}
                            className='w-fit'
                        >
                            Add Page
                        </StanButton>
                        {isAddDisabled && addDisabledReason && (
                            <p className='text-caption-1-regular text-gray'>{addDisabledReason}</p>
                        )}
                    </div>
                </div>
            </div>

            <ManageFunnelPagesModal
                isOpen={showManageModal}
                onClose={() => setShowManageModal(false)}
                pages={savedPages}
                productInfoByPageId={productInfoByPageId}
                isDeleting={isDeleting}
                onConfirmReorder={onReorderPages}
                onHidePage={onHidePage}
                onDuplicatePage={onDuplicatePage}
                onDeletePage={onDeletePage}
            />
        </div>
    );
};
